import log from '@src/middleware/loglevel';
import moment from 'moment';
import { getLoggedInUserId } from '@src/config/myAtupriApi';
import { API_GET_OKP_TARIFFS, API_GET_UTI_TARIFFS, API_GET_VVG_TARIFFS } from '@src/api/tariff.api';
import userService from '@src/api/myAtupriUserService.api';
import { getPerson } from '@src/util/submission';
import { deepMerge } from '@src/util/data';
import { requestUpdateSubmission, showSubmissionError } from '@src/ducks/submission.duck';
import { getUserFlow, UserFlowEnum } from '@src/util/process';

const requestAction = actionType => params => ({ type: actionType, payload: { params } });
const successAction = actionType => result => ({ type: actionType, payload: result });
const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

const TAB_PRODUCT = 'product';
const TAB_CONFIRMATION = 'confirmation';

// ================ Action types ================ //

const FETCH_OKP_TARIFF_REQUEST = 'app/FETCH_OKP_TARIFF_REQUEST';
const FETCH_OKP_TARIFF_SUCCESS = 'app/FETCH_OKP_TARIFF_SUCCESS';
const FETCH_OKP_TARIFF_ERROR = 'app/FETCH_OKP_TARIFF_ERROR';

const FETCH_VVG_TARIFF_REQUEST = 'app/FETCH_VVG_TARIFF_REQUEST';
const FETCH_VVG_TARIFF_SUCCESS = 'app/FETCH_VVG_TARIFF_SUCCESS';
const FETCH_VVG_TARIFF_ERROR = 'app/FETCH_VVG_TARIFF_ERROR';

const FETCH_UTI_TARIFF_REQUEST = 'app/FETCH_UTI_TARIFF_REQUEST';
const FETCH_UTI_TARIFF_SUCCESS = 'app/FETCH_UTI_TARIFF_SUCCESS';
const FETCH_UTI_TARIFF_ERROR = 'app/FETCH_UTI_TARIFF_ERROR';

const RESET_TARIFFS_REQUEST = 'app/RESET_TARIFFS_REQUEST';

const FETCH_CURRENT_PRODUCTS_REQUEST = 'app/myAtupri/FETCH_CURRENT_PRODUCTS_REQUEST';
const FETCH_CURRENT_PRODUCTS_SUCCESS = 'app/myAtupri/FETCH_CURRENT_PRODUCTS_SUCCESS';
const FETCH_CURRENT_PRODUCTS_ERROR = 'app/myAtupri/FETCH_CURRENT_PRODUCTS_ERROR';

const FETCH_EFFECTIVE_DATES_REQUEST = 'app/myAtupri/FETCH_EFFECTIVE_DATE_REQUEST';
const FETCH_EFFECTIVE_DATES_SUCCESS = 'app/myAtupri/FETCH_EFFECTIVE_DATE_SUCCESS';
const FETCH_EFFECTIVE_DATES_ERROR = 'app/myAtupri/FETCH_EFFECTIVE_DATE_ERROR';

// ================ Reducer ================ //

const initialState = {
    blockInterface: false,
    fetchTariffInProgress: false,
    fetchOkpTariffInProgress: false,
    fetchVvgTariffInProgress: false,
    fetchUtiTariffInProgress: false,
    fetchCurrentProductsInProgress: false,
    fetchOkpTariffError: null,
    fetchVvgTariffError: null,
    fetchUtiTariffError: null,
    okpTariffs: null,
    vvgTariffs: null,
    utiTariffs: null,
    loggedInUser: getLoggedInUserId(),
    currentProducts: null,
};

export default function apiDataReducer(state = initialState, action = {}) {
    const { type, payload } = action;

    switch (type) {
        case FETCH_OKP_TARIFF_REQUEST:
            return {
                ...state,
                blockInterface: !!payload.params.blockInterface,
                fetchOkpTariffInProgress: true,
                fetchOkpTariffError: null,
                fetchTariffInProgress: !!payload.params.showLoader,
            };
        case FETCH_OKP_TARIFF_SUCCESS:
            return {
                ...state,
                blockInterface:
                    !!payload.blockInterface && (state.fetchVvgTariffInProgress || state.fetchUtiTariffInProgress),
                fetchOkpTariffInProgress: false,
                fetchOkpTariffError: null,
                fetchTariffInProgress:
                    !!payload.showLoader && (state.fetchVvgTariffInProgress || state.fetchUtiTariffInProgress),
                okpTariffs: payload.okpTariffs,
            };
        case FETCH_OKP_TARIFF_ERROR:
            return {
                ...state,
                blockInterface: false,
                fetchOkpTariffInProgress: true,
                fetchOkpTariffError: payload,
                fetchTariffInProgress: !!payload.showLoader,
            };

        case FETCH_VVG_TARIFF_REQUEST:
            return {
                ...state,
                blockInterface: !!payload.params.blockInterface,
                fetchVvgTariffInProgress: true,
                fetchVvgTariffError: null,
                fetchTariffInProgress: !!payload.params.showLoader,
            };
        case FETCH_VVG_TARIFF_SUCCESS:
            return {
                ...state,
                blockInterface:
                    !!payload.blockInterface && (state.fetchOkpTariffInProgress || state.fetchUtiTariffInProgress),
                fetchVvgTariffInProgress: false,
                fetchVvgTariffError: null,
                fetchTariffInProgress:
                    !!payload.showLoader && (state.fetchOkpTariffInProgress || state.fetchUtiTariffInProgress),
                vvgTariffs: payload.vvgTariffs,
            };
        case FETCH_VVG_TARIFF_ERROR:
            return {
                ...state,
                blockInterface: false,
                fetchVvgTariffInProgress: true,
                fetchVvgTariffError: payload,
                fetchTariffInProgress: !!payload.showLoader,
            };

        case FETCH_UTI_TARIFF_REQUEST:
            return {
                ...state,
                blockInterface: !!payload.params.blockInterface,
                fetchUtiTariffInProgress: true,
                fetchUtiTariffError: null,
                fetchTariffInProgress: !!payload.params.showLoader,
            };
        case FETCH_UTI_TARIFF_SUCCESS:
            return {
                ...state,
                blockInterface:
                    !!payload.blockInterface && (state.fetchOkpTariffInProgress || state.fetchVvgTariffInProgress),
                fetchUtiTariffInProgress: false,
                fetchUtiTariffError: null,
                fetchTariffInProgress:
                    !!payload.showLoader && (state.fetchOkpTariffInProgress || state.fetchVvgTariffInProgress),
                utiTariffs: payload.utiTariffs,
            };
        case FETCH_UTI_TARIFF_ERROR:
            return {
                ...state,
                blockInterface: false,
                fetchUtiTariffInProgress: true,
                fetchUtiTariffError: payload,
                fetchTariffInProgress: !!payload.showLoader,
            };
        case RESET_TARIFFS_REQUEST: {
            const newState = state;
            delete newState.okpTariffs;
            delete newState.vvgTariffs;
            delete newState.utiTariffs;

            return newState;
        }
        case FETCH_CURRENT_PRODUCTS_REQUEST:
            return {
                ...state,
                fetchCurrentProductsInProgress: true,
            };
        case FETCH_CURRENT_PRODUCTS_SUCCESS:
            return {
                ...state,
                fetchCurrentProductsInProgress: false,
                currentProducts: payload,
            };
        case FETCH_EFFECTIVE_DATES_REQUEST:
            return {
                ...state,
                fetchTariffInProgress: !!payload.params.showLoader,
            };
        case FETCH_EFFECTIVE_DATES_SUCCESS:
            return {
                ...state,
                effectiveDates: payload,
                fetchTariffInProgress: false,
            };
        case FETCH_EFFECTIVE_DATES_ERROR:
            return {
                ...state,
                fetchEffectiveDatesError: payload,
            };
        default:
            return state;
    }
}

// ================ Action creators ================ //

const fetchOkpTariff = requestAction(FETCH_OKP_TARIFF_REQUEST);
const fetchOkpTariffSuccess = successAction(FETCH_OKP_TARIFF_SUCCESS);
const fetchOkpTariffError = errorAction(FETCH_OKP_TARIFF_ERROR);

const fetchVvgTariff = requestAction(FETCH_VVG_TARIFF_REQUEST);
const fetchVvgTariffSuccess = successAction(FETCH_VVG_TARIFF_SUCCESS);
const fetchVvgTariffError = errorAction(FETCH_VVG_TARIFF_ERROR);

const fetchUtiTariff = requestAction(FETCH_UTI_TARIFF_REQUEST);
const fetchUtiTariffSuccess = successAction(FETCH_UTI_TARIFF_SUCCESS);
const fetchUtiTariffError = errorAction(FETCH_UTI_TARIFF_ERROR);

const resetTariffs = requestAction(RESET_TARIFFS_REQUEST);

const fetchCurrentProducts = requestAction(FETCH_CURRENT_PRODUCTS_REQUEST);
const fetchCurrentProductsSuccess = successAction(FETCH_CURRENT_PRODUCTS_SUCCESS);
const fetchCurrentProductsError = errorAction(FETCH_CURRENT_PRODUCTS_ERROR);

const fetchEffectiveDates = requestAction(FETCH_EFFECTIVE_DATES_REQUEST);
const fetchEffectiveDatesSuccess = successAction(FETCH_EFFECTIVE_DATES_SUCCESS);
const fetchEffectiveDatesError = errorAction(FETCH_EFFECTIVE_DATES_ERROR);

// ================ Thunk ================ //

export function requestFetchTariff(actionPayload) {
    return async (dispatch, getState) => {
        let promises = [];
        const { submissionId, personId, okp = false, vvg = false, uti = false } = actionPayload;
        let submission = getState().premcalcData.entities.ownSubmission[submissionId];
        const persons = submission.relationships.persons;

        if (okp) {
            dispatch(fetchOkpTariff(actionPayload));
            promises.push(
                API_GET_OKP_TARIFFS(personId, persons, submission.attributes?.collectiveAgreementNumber)
                    .then(result => {
                        return result;
                    })
                    .catch(e => {
                        dispatch(fetchOkpTariffError({ ...e, ...actionPayload }));
                    })
            );
        }

        if (vvg) {
            dispatch(fetchVvgTariff(actionPayload));

            const collectiveAgreementNumber = submission.attributes?.collectiveAgreementNumber;
            const personBasket = submission.relationships.basket[personId];
            let okpProduct = Array.isArray(personBasket.okp) && personBasket.okp[0];
            if (!okpProduct && submission?.relationships?.persons?.[personId]?.okpPreselectedProduct) {
                okpProduct = submission?.relationships?.persons?.[personId]?.okpPreselectedProduct;
            }
            promises.push(
                API_GET_VVG_TARIFFS(personId, persons, okpProduct?.product, collectiveAgreementNumber)
                    .then(async result => {
                        return result;
                    })
                    .catch(e => {
                        dispatch(fetchVvgTariffError({ ...e, ...actionPayload }));
                    })
            );
        }

        if (uti) {
            dispatch(fetchUtiTariff(actionPayload));

            promises.push(
                API_GET_UTI_TARIFFS(personId, persons, submission.attributes?.collectiveAgreementNumber)
                    .then(result => {
                        return result;
                    })
                    .catch(e => {
                        dispatch(fetchUtiTariffError({ ...e, ...actionPayload }));
                    })
            );
        }

        return Promise.all(promises).then(data => {
            submission = getState().premcalcData.entities.ownSubmission[submissionId];
            data = deepMerge(data);

            if (data.okp) {
                dispatch(fetchOkpTariffSuccess({ okpTariffs: data.okp, ...actionPayload }));
            }
            if (data.vvg) {
                dispatch(fetchVvgTariffSuccess({ vvgTariffs: data.vvg, ...actionPayload }));
            }
            if (data.uti) {
                dispatch(fetchUtiTariffSuccess({ utiTariffs: data.uti, ...actionPayload }));
            }
        });
    };
}

export function requestResetTariffs(actionPayload) {
    return dispatch => {
        dispatch(resetTariffs({ ...actionPayload }));
    };
}

const handleTabProduct = (dispatch, submission, params) => {
    const firstPerson = Array.isArray(submission?.relationships?.persons) && submission.relationships.persons[0];
    const personId = params.personId || firstPerson?.id;
    if (!personId) return false;

    const person = getPerson(submission, personId);
    if (!person) {
        dispatch(showSubmissionError({ message: 'personId is not valid' }));
        return false;
    }

    const fetchTarifPayload = {
        submissionId: submission.id.uuid,
        personId: person.id,
        okp: true,
        vvg: true,
        uti: params.subpanel === 'recommend' || getLoggedInUserId(submission),
        showLoader: true,
    };
    dispatch(requestFetchTariff(fetchTarifPayload));
    return true;
};

const handleTabConfirmation = (dispatch, submissionId, submission) => {
    const birthdate = moment(submission.relationships.persons[0].birthdate);
    const today = moment();

    // Calculate age based on whether they've had their birthday this year
    const birthdayThisYear = birthdate.clone().year(today.year());
    const hasHadBirthdayThisYear = birthdayThisYear.isSameOrBefore(today, 'day');
    const age = today.year() - birthdate.year();

    // For anyone turning 19 next year, the getEffectiveDateCall will not work. For everyone else, it can be executed.
    // The ESB sets today's date as valid_from for the date check. Therefore, the age must be checked a little more broadly.
    const performEffectiveDateCall =
        age < 17 || // will be 17 or 18 next year
        (age === 17 && hasHadBirthdayThisYear) || // will turn 18 next year
        age >= 19; // will be 20 or older next year

    if (
        getLoggedInUserId(submission) &&
        getUserFlow(submission) !== UserFlowEnum.standard &&
        performEffectiveDateCall
    ) {
        dispatch(requestEffectiveDates(submissionId));
    }
};

export const afterInitialDataLoaded = (dispatch, response, params) => {
    const submission = response?.[1]?.data;
    if (submission) {
        switch (params.tab) {
            case TAB_PRODUCT:
                handleTabProduct(dispatch, submission, params);
                break;
            case TAB_CONFIRMATION:
                handleTabConfirmation(dispatch, submission.id.uuid, submission);
                break;
            default:
                break;
        }

        if (getLoggedInUserId(submission)) {
            dispatch(requestCurrentProducts(submission));
        }
    }

    return response;
};

export function requestCurrentProducts(submission) {
    return dispatch => {
        dispatch(fetchCurrentProducts({}));
        if (submission.attributes.loggedInUserInsuredNr) {
            // get subscribed products for all users
            userService
                .loadSubscribedProducts(submission)
                .then(response => {
                    dispatch(fetchCurrentProductsSuccess(response.data));
                })
                .catch(e => {
                    dispatch(fetchCurrentProductsError(e));
                    log.debug('=== MYATUPRI_API_GET_SUBSCRIBED_PRODUCTS ===', {
                        e,
                    });
                });
        }
    };
}

export function requestEffectiveDates(submissionId) {
    return (dispatch, getState) => {
        dispatch(
            fetchEffectiveDates({
                showLoader: true,
            })
        );
        let submission = getState().premcalcData.entities.ownSubmission[submissionId];
        let promises = [];

        submission?.relationships?.persons?.forEach(person => {
            const personBasket = submission.relationships.basket[person.id];
            const currentProduct = getState().apiData.currentProducts?.find(
                currentProduct => currentProduct.insuredNr === person.insuranceNumber
            );
            const currentObjectIdOkp = currentProduct?.products.find(product => product.code === 'OKP')?.tariffId;
            personBasket.okp?.forEach(product => {
                promises.push(
                    userService
                        .loadEffectiveDates(person.insuranceNumber, product.tariff, currentObjectIdOkp)
                        .then(response => {
                            // update the insuranceStart in the Redux state within the person object
                            submission.relationships.persons.find(
                                personObject => personObject.id === person.id
                            ).insuranceStart = response.data.validFrom;

                            requestUpdateSubmission(undefined, submission);
                            return {
                                personId: person.id,
                                insuranceNumber: person.insuranceNumber,
                                currentObjectId: currentObjectIdOkp,
                                newObjectId: product.tariff,
                                validFrom: response.data.validFrom,
                            };
                        })
                );
            });
            personBasket.vvg?.forEach(product => {
                promises.push(
                    userService.loadEffectiveDates(person.insuranceNumber, product.tariff, undefined).then(response => {
                        return {
                            personId: person.id,
                            insuranceNumber: person.insuranceNumber,
                            newObjectId: product.tariff,
                            validFrom: response.data.validFrom,
                        };
                    })
                );
            });
        });

        return Promise.all(promises)
            .then(effectiveDates => {
                dispatch(fetchEffectiveDatesSuccess(effectiveDates));
            })
            .catch(e => {
                dispatch(fetchEffectiveDatesError(e));
            });
    };
}
